// pages/HubSpotCallback.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, CheckCircle, XCircle } from "lucide-react";
import apiService from "../services/api";

const HubSpotCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("processing"); // processing, success, error

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const error = params.get("error");
      const errorDescription = params.get("error_description");

      if (error) {
        console.error("OAuth error:", error, errorDescription);
        setStatus("error");
        setTimeout(() => {
          if (window.opener) {
            window.opener.postMessage(
              {
                type: "hubspot-error",
                error: errorDescription || error,
              },
              "*"
            );
            window.close();
          } else {
            navigate("/settings?tab=integrations");
          }
        }, 2000);
        return;
      }

      if (!code) {
        console.error("No authorization code received");
        setStatus("error");
        setTimeout(() => {
          if (window.opener) {
            window.opener.postMessage(
              { type: "hubspot-error", error: "No authorization code" },
              "*"
            );
            window.close();
          } else {
            navigate("/settings?tab=integrations");
          }
        }, 2000);
        return;
      }

      try {
        await apiService.completeHubSpotOAuth(code);
        setStatus("success");

        // Notify parent window
        if (window.opener) {
          window.opener.postMessage({ type: "hubspot-connected" }, "*");
        }

        // Close popup after short delay
        setTimeout(() => {
          if (window.opener) {
            window.close();
          } else {
            navigate("/settings?tab=integrations");
          }
        }, 1500);
      } catch (err) {
        console.error("Callback error:", err);
        setStatus("error");
        setTimeout(() => {
          if (window.opener) {
            window.opener.postMessage(
              {
                type: "hubspot-error",
                error: err.response?.data?.message || "Connection failed",
              },
              "*"
            );
            window.close();
          } else {
            navigate("/settings?tab=integrations");
          }
        }, 2000);
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-[#0C0E12] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {status === "processing" && (
          <>
            <RefreshCw className="w-16 h-16 text-purple-500 animate-spin mx-auto mb-6" />
            <h2 className="text-white text-2xl mb-3 font-semibold">
              Connecting HubSpot...
            </h2>
            <p className="text-gray-400 text-sm">
              Please wait while we complete the setup
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-white text-2xl mb-3 font-semibold">
              Connected Successfully!
            </h2>
            <p className="text-gray-400 text-sm">
              HubSpot has been connected to your account
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-white text-2xl mb-3 font-semibold">
              Connection Failed
            </h2>
            <p className="text-gray-400 text-sm">
              There was an error connecting HubSpot. Please try again.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default HubSpotCallback;
